/**
 * Tranzit carrier directory API client (Phase 1).
 * Align paths with backend/src/config/tranzitCarrierDirectory.ts
 */

import { getApiUrl } from '../utils/backendUrl';
import {
  TRANZIT_API_CARRIER_DIRECTORY_AVAILABLE_NEAR_PATH,
  TRANZIT_API_CARRIER_DIRECTORY_LIST_PATH,
  TRANZIT_API_CARRIER_DIRECTORY_PROFILE_PATH,
  type TranzitCarrierDirectoryRowKey
} from '../constants/tranzitCarrierDirectory';
import Logger from './logger';
import { mapOperationalAccessApiError } from '../utils/operationalAccessMessages';

/** getApiUrl() already ends with `/api`; backend routes mount at `/api/tranzit/*`. */
function tranzitApiUrl(path: string): string {
  return `${getApiUrl().replace(/\/$/, '')}/tranzit${path}`;
}

export type TranzitCarrierDirectoryRow = Partial<Record<TranzitCarrierDirectoryRowKey, unknown>> & {
  id: string;
};

export type TranzitAvailableCarrierRow = TranzitCarrierDirectoryRow & { distance: number };

async function authHeaders(idToken: string): Promise<HeadersInit> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`
  };
}

export async function fetchTranzitCarrierDirectoryList(
  idToken: string
): Promise<{ success: boolean; carriers?: TranzitCarrierDirectoryRow[]; error?: string }> {
  try {
    const res = await fetch(tranzitApiUrl(TRANZIT_API_CARRIER_DIRECTORY_LIST_PATH), {
      method: 'POST',
      headers: await authHeaders(idToken),
      body: JSON.stringify({})
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: mapOperationalAccessApiError(data, `List failed (${res.status})`)
      };
    }
    return { success: true, carriers: (data.carriers ?? []) as TranzitCarrierDirectoryRow[] };
  } catch (error) {
    Logger.error('[tranzitCarrierDirectoryApi] list failed', error as Error);
    return { success: false, error: 'Failed to load carrier directory.' };
  }
}

export async function fetchTranzitAvailableCarriersNear(
  idToken: string,
  params: { anchorLng: number; anchorLat: number; radiusMiles: number }
): Promise<{ success: boolean; carriers?: TranzitAvailableCarrierRow[]; error?: string }> {
  try {
    const res = await fetch(tranzitApiUrl(TRANZIT_API_CARRIER_DIRECTORY_AVAILABLE_NEAR_PATH), {
      method: 'POST',
      headers: await authHeaders(idToken),
      body: JSON.stringify(params)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: mapOperationalAccessApiError(data, `Available carriers failed (${res.status})`)
      };
    }
    return { success: true, carriers: (data.carriers ?? []) as TranzitAvailableCarrierRow[] };
  } catch (error) {
    Logger.error('[tranzitCarrierDirectoryApi] available-near failed', error as Error);
    return { success: false, error: 'Failed to load available carriers.' };
  }
}

export async function fetchTranzitUserProfileViaApi(
  idToken: string,
  targetUserId: string
): Promise<{ success: boolean; profile?: TranzitCarrierDirectoryRow; error?: string }> {
  try {
    const res = await fetch(
      `${tranzitApiUrl(TRANZIT_API_CARRIER_DIRECTORY_PROFILE_PATH)}/${encodeURIComponent(targetUserId)}`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` }
      }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: mapOperationalAccessApiError(data, `Profile failed (${res.status})`)
      };
    }
    return { success: true, profile: data.profile as TranzitCarrierDirectoryRow };
  } catch (error) {
    Logger.error('[tranzitCarrierDirectoryApi] profile failed', error as Error);
    return { success: false, error: 'Failed to load user profile.' };
  }
}
